import React, { useState } from "react";
import useSWR from "swr";
import {
  Box, Button, Card, Modal, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
  FormControl, InputLabel, Select, MenuItem, Chip,
  Snackbar, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
  Switch, FormControlLabel, Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { apiHost } from '../utils/apiHost';
import { namaBerkasKategori, pesanGalatEkspor } from '../utils/eksporProduk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) =>
  axios.get(url, { withCredentials: true }).then((res) =>
    Array.isArray(res.data.data) ? res.data.data : []
  );

const branchesFetcher = (url) =>
  axios.get(url, { withCredentials: true }).then((res) => {
    const d = res.data?.data ?? res.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === "object" && !Array.isArray(d)) return [d];
    return [];
  });

export const Category = () => {
  const { data: categorys, error: CategoryError, mutate } = useSWR(
    `${getApiBaseUrl()}/kategori`, fetcher
  );

  const { data: branches } = useSWR(
    `${getApiBaseUrl()}/cabangbyrole?bentuk=daftar`, branchesFetcher
  );

  const { user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ namakategori: "", cabanguuid: "", tampilDiAplikasi: true, tampilDiMenu: false });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = user?.role === "admin";

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Ekspor daftar kategori (+ jumlah produk) ke Excel; dibuat server dan tercatat di Log Aktivitas.
  const [mengekspor, setMengekspor] = useState(false);
  const eksporExcel = async () => {
    setMengekspor(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/export-kategori`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", namaBerkasKategori(isAdmin ? branches?.[0]?.namacabang : "semua"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar(await pesanGalatEkspor(error, "Gagal mengekspor kategori"), "error");
    } finally {
      setMengekspor(false);
    }
  };

  const getCabangNama = (cabanguuid) => {
    if (!cabanguuid || !branches || !Array.isArray(branches)) return null;
    const found = branches.find((b) => b.uuid === cabanguuid);
    return found ? found.namacabang : null;
  };

  const handleOpen = () => {
    setFormData({
      namakategori: "",
      cabanguuid: isAdmin ? (user.cabanguuid || "") : "",
      tampilDiAplikasi: true,
      tampilDiMenu: false,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ namakategori: "", cabanguuid: "", tampilDiAplikasi: true, tampilDiMenu: false });
    setIsEditing(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.namakategori.trim()) {
      showSnackbar("Nama kategori tidak boleh kosong", "error");
      return;
    }
    try {
      if (isEditing) {
        const payload = {
          namakategori: formData.namakategori,
          tampilDiAplikasi: formData.tampilDiAplikasi,
          tampilDiMenu: formData.tampilDiMenu,
        };
        if (isSuperAdmin) {
          payload.cabanguuid = formData.cabanguuid || null;
        }
        await axios.put(
          `${getApiBaseUrl()}/updatekategori/${editId}`,
          payload,
          { withCredentials: true }
        );
        showSnackbar("Kategori berhasil diperbarui");
      } else {
        await axios.post(
          `${getApiBaseUrl()}/createkategori`,
          {
            namakategori: formData.namakategori,
            cabanguuid: formData.cabanguuid || null,
            tampilDiAplikasi: formData.tampilDiAplikasi,
            tampilDiMenu: formData.tampilDiMenu,
          },
          { withCredentials: true }
        );
        showSnackbar("Kategori berhasil ditambahkan");
      }
      mutate();
      handleClose();
    } catch (error) {
      const msg = error.response?.data?.message || "Terjadi kesalahan";
      showSnackbar(msg, "error");
    }
  };

  const handleEdit = (kategori) => {
    setFormData({
      namakategori: kategori.namakategori,
      cabanguuid: kategori.cabanguuid || "",
      tampilDiAplikasi: kategori.tampilDiAplikasi !== false,
      tampilDiMenu: kategori.tampilDiMenu !== false,
    });
    setEditId(kategori.uuid);
    setIsEditing(true);
    setOpen(true);
  };

  const handleQuickToggle = async (kategori, field, value) => {
    try {
      await axios.put(
        `${getApiBaseUrl()}/updatekategori/${kategori.uuid}`,
        { namakategori: kategori.namakategori, [field]: value },
        { withCredentials: true }
      );
      mutate();
    } catch (error) {
      const msg = error.response?.data?.message || "Gagal memperbarui status";
      showSnackbar(msg, "error");
    }
  };

  const handleDeleteConfirm = (id) => {
    setConfirmDelete({ open: true, id });
  };

  const handleDeleteCancel = () => {
    setConfirmDelete({ open: false, id: null });
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${getApiBaseUrl()}/deletekategori/${confirmDelete.id}`,
        { withCredentials: true }
      );
      mutate();
      showSnackbar("Kategori berhasil dihapus");
    } catch (error) {
      const msg = error.response?.data?.message || "Gagal menghapus data";
      showSnackbar(msg, "error");
    } finally {
      handleDeleteCancel();
    }
  };

  if (CategoryError) {
    return <Typography color="error">Gagal memuat kategori: {CategoryError.message}</Typography>;
  }

  if (!categorys) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(categorys)) {
    return <Typography color="error">Format data kategori tidak valid.</Typography>;
  }

  return (
    <Box sx={{ padding: 2, overflowX: "auto" }}>
      <Typography variant="h6" gutterBottom>List Kategori</Typography>

      {(isSuperAdmin || isAdmin) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Tambah
          </Button>
          <Button variant="outlined" color="success" onClick={eksporExcel} disabled={mengekspor}>
            {mengekspor ? "Mengekspor..." : "Ekspor Excel"}
          </Button>
        </Box>
      )}

      <Card>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Nama Kategori</TableCell>
                {isSuperAdmin && <TableCell>Cabang</TableCell>}
                <TableCell align="center">Aplikasi POS</TableCell>
                <TableCell align="center">Web Menu</TableCell>
                {(isSuperAdmin || isAdmin) && <TableCell>Aksi</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {categorys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 6 : 5} align="center">
                    Belum ada kategori
                  </TableCell>
                </TableRow>
              ) : (
                categorys.map((kategori, index) => {
                  const namaCabang = getCabangNama(kategori.cabanguuid);
                  return (
                    <TableRow key={kategori.uuid}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{kategori.namakategori}</TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          {namaCabang ? (
                            <Chip label={namaCabang} size="small" color="primary" variant="outlined" />
                          ) : (
                            <Chip label="Semua Cabang" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Tooltip title="Tampil/sembunyikan produk kategori ini di aplikasi POS">
                          <span>
                            <Switch
                              size="small"
                              checked={kategori.tampilDiAplikasi !== false}
                              disabled={!(isSuperAdmin || isAdmin)}
                              onChange={(e) => handleQuickToggle(kategori, 'tampilDiAplikasi', e.target.checked)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Tampil/sembunyikan produk kategori ini di web menu customer">
                          <span>
                            <Switch
                              size="small"
                              checked={kategori.tampilDiMenu !== false}
                              disabled={!(isSuperAdmin || isAdmin)}
                              onChange={(e) => handleQuickToggle(kategori, 'tampilDiMenu', e.target.checked)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      {(isSuperAdmin || isAdmin) && (
                        <TableCell>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(kategori)}
                            sx={{ mr: 1 }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleDeleteConfirm(kategori.uuid)}
                          >
                            Hapus
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", width: 340,
          bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 2,
        }}>
          <Typography variant="h6" gutterBottom>
            {isEditing ? "Edit Kategori" : "Tambah Kategori"}
          </Typography>

          <TextField
            label="Nama Kategori"
            name="namakategori"
            value={formData.namakategori}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          {isSuperAdmin && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Cabang</InputLabel>
              <Select
                name="cabanguuid"
                value={formData.cabanguuid}
                onChange={handleChange}
                label="Cabang"
              >
                <MenuItem value="">Semua Cabang</MenuItem>
                {(Array.isArray(branches) ? branches : []).map((branch) => (
                  <MenuItem key={branch.uuid} value={branch.uuid}>
                    {branch.namacabang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isAdmin && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Kategori akan ditambahkan ke cabang Anda.
            </Typography>
          )}

          <FormControlLabel
            sx={{ display: "flex", mt: 1 }}
            control={
              <Switch
                checked={formData.tampilDiAplikasi}
                onChange={(e) => setFormData((prev) => ({ ...prev, tampilDiAplikasi: e.target.checked }))}
              />
            }
            label="Tampil di Aplikasi POS"
          />
          <FormControlLabel
            sx={{ display: "flex" }}
            control={
              <Switch
                checked={formData.tampilDiMenu}
                onChange={(e) => setFormData((prev) => ({ ...prev, tampilDiMenu: e.target.checked }))}
              />
            }
            label="Tampil di Web Menu"
          />
          {!isEditing && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Kategori baru default tidak tampil di web menu sampai diaktifkan.
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button variant="outlined" color="inherit" onClick={handleClose} fullWidth>
              Batal
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
              {isEditing ? "Update" : "Simpan"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={confirmDelete.open} onClose={handleDeleteCancel}>
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Yakin ingin menghapus kategori ini? Data yang dihapus tidak dapat dikembalikan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Batal</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};